import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSession } from '@/hooks/useSession';
import {
  selectCurrentGroupId,
  selectAllWorkspaces,
  selectActiveWorkspace,
  selectOldUserDetails,
  selectUserRole,
} from '@/store/selectors';
import { useNotifications } from './useNotifications';
import { NavSearch } from './NavSearch';
import { NavNotifications } from './NavNotifications';
import { NavWorkspaces } from './NavWorkspaces';
import { NavUser } from './NavUser';
import logoSvg from '@/assets/yemma-logo-navbar.svg?url';

type DropdownKey = 'search' | 'notifications' | 'workspaces' | 'user';

interface NavBarProps {
  updateModalOpen: (key: string | undefined) => void;
}

export default function NavBar({ updateModalOpen }: NavBarProps) {
  const [activeDropdown, setActiveDropdown] = useState<DropdownKey | null>(null);

  const session = useSession();
  const currentUserGroup = useSelector(selectCurrentGroupId);
  const allWorkspaces = useSelector(selectAllWorkspaces);
  const activeWorkspace = useSelector(selectActiveWorkspace);
  const oldUserDetails = useSelector(selectOldUserDetails);
  const userRole = useSelector(selectUserRole);

  const { allMessages, currentGroupMessages } = useNotifications();

  const isRootWorkspace = useMemo(
    () => activeWorkspace?.parent === 'root',
    [activeWorkspace],
  );

  const hasWorkspaces =
    session.groupsList && session.groupsList.length > 0;

  const shouldRender = session.isAuthenticated && oldUserDetails;

  const toggle = useCallback(
    (key: DropdownKey) =>
      setActiveDropdown((prev) => (prev === key ? null : key)),
    [],
  );

  const close = useCallback(() => setActiveDropdown(null), []);

  return (
    <nav className="sticky top-0 z-50 flex h-14 items-center border-b border-border/60 bg-card/80 px-4 backdrop-blur-xl">
      {/* Logo */}
      {session.isAuthenticated ? (
        isRootWorkspace ? (
          <span className="flex-shrink-0">
            <img src={logoSvg} alt="Yemma" className="h-8 w-auto" />
          </span>
        ) : (
          <Link to="/in/folder/root" className="flex-shrink-0">
            <img src={logoSvg} alt="Yemma" className="h-8 w-auto" />
          </Link>
        )
      ) : (
        <a
          href="https://www.theyemma.com/"
          title="Yemma"
          className="flex-shrink-0"
        >
          <img src={logoSvg} alt="Yemma" className="h-8 w-auto" />
        </a>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Authenticated nav items */}
      {shouldRender && (
        <div className="flex items-center gap-1">
          {hasWorkspaces && !isRootWorkspace && (
            <>
              <NavSearch
                open={activeDropdown === 'search'}
                onToggle={() => toggle('search')}
                onClose={close}
              />
              <NavNotifications
                open={activeDropdown === 'notifications'}
                onToggle={() => toggle('notifications')}
                onClose={close}
                messages={currentGroupMessages}
              />
            </>
          )}
          {hasWorkspaces && (
            <NavWorkspaces
              open={activeDropdown === 'workspaces'}
              onToggle={() => toggle('workspaces')}
              onClose={close}
              notifications={allMessages}
            />
          )}
          <NavUser
            open={activeDropdown === 'user'}
            onToggle={() => toggle('user')}
            onClose={close}
            updateModalOpen={updateModalOpen}
          />
        </div>
      )}

      {/* Unauthenticated links */}
      {!session.isAuthenticated && userRole !== 'guest' && (
        <div className="flex items-center gap-3">
          <Link
            to="/signup"
            className="text-sm font-semibold text-primary underline transition-colors hover:text-primary-hover"
          >
            Sign up
          </Link>
          <Link
            to="/login"
            className="text-sm font-semibold text-primary underline transition-colors hover:text-primary-hover"
          >
            Login
          </Link>
        </div>
      )}
    </nav>
  );
}
